import {inject, Injectable} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MAT_DIALOG_DATA} from '@angular/material/dialog';
import {ApiExamInterface, ApiExamPlanInterface, AppExamService, ExamAssignInterface} from '@component/exam/exam.service';
import {BookListInterface, BookListItemInterface} from '@shared/interfaces/book.interface';
import {BehaviorSubject, Observable, of, Subject, Subscription, zip} from 'rxjs';
import {GroupInterface, GroupsInterface} from '@shared/interfaces/group.interface';
import {HttpMethodsEnum} from '@core/http/methods.enum';
import {TranslateService} from '@ngx-translate/core';
import {map, switchMap, take} from 'rxjs/operators';
import {HttpErrorResponseInterface, HttpValidationErrorResponseInterface} from '@core/interfaces/http-error-response.interface';
import {BookApiService} from '@core/services/book/book-api.service';
import {GroupsService} from '@core/services/groups/groups.service';
import {ApiExamsResponseInterface} from '@store/exam/exam-api.contract';
import {HttpErrorResponse} from '@angular/common/http';

export interface AssignDialogAssignResultInterface {
    status: boolean;
    errorResponse?: HttpErrorResponse|HttpValidationErrorResponseInterface;
}

export interface AssignDialogDataInterface {
    examPlan?: ApiExamPlanInterface;
}

export interface AssignDialogStateInterface {
    loading: boolean;
    book?: BookListItemInterface;
    books: BookListItemInterface[];
    exam?: ApiExamInterface;
    exams: ApiExamInterface[];
    groups: GroupInterface[];
}

const defaultState: AssignDialogStateInterface = {
    loading: true,
    books: [],
    exams: [],
    groups: [],
}

@Injectable()
export class AssignDialogService {
    readonly #subscriptions: Subscription[] = [];

    readonly #translateService: TranslateService = inject(TranslateService);

    readonly #snackBar: MatSnackBar = inject(MatSnackBar);

    readonly #dialogData: AssignDialogDataInterface = inject(MAT_DIALOG_DATA);

    readonly #stateSubject: BehaviorSubject<AssignDialogStateInterface>;

    readonly #assignSubject: Subject<AssignDialogAssignResultInterface> = new Subject<AssignDialogAssignResultInterface>();

    readonly #bookService: BookApiService = inject(BookApiService);

    readonly #groupService: GroupsService = inject(GroupsService);

    readonly #examService: AppExamService = inject(AppExamService);

    #state: AssignDialogStateInterface;

    constructor() {
        this.#stateSubject = new BehaviorSubject<AssignDialogStateInterface>(this.#state = {...defaultState});

        // todo - this should become the default
        this.#bookService.retrieveBooks(0,-1).pipe(
            map((list: BookListInterface) => this.#stateSubject.next(this.#state = {...this.#state, books: list.books})),
            switchMap(() => undefined !== this.#dialogData.examPlan ? this.#handleBook(this.#dialogData.examPlan.book.uuid) : of(undefined)),
        ).subscribe(() => this.#stateSubject.next(this.#state = {...this.#state, loading: false}));
    }

    subscribe(fn: (state: AssignDialogStateInterface) => void): void {
        this.#subscriptions.push(
            this.#stateSubject.subscribe((state: AssignDialogStateInterface) => fn(state)),
        );
    }

    #handleBook(bookUuid: string): Observable<void> {
        return zip<[GroupsInterface, ApiExamInterface[]]>(
            this.#groupService.retrieveGroups(bookUuid),
            this.#examService.fetchByBookUuid(bookUuid, [20]).pipe(
                map((response: ApiExamsResponseInterface) => response.exams),
            ),
        ).pipe(
            map(([groupResponse, exams]) => {
                this.#state = {
                    ...this.#state,
                    groups: groupResponse.groups.filter(
                        (group: GroupInterface) => (group.students?.length ?? 0) > 0,
                    ),
                    exams,
                };

                this.#stateSubject.next(this.#state);
            }),
        );
    }

    unsubscribe(): void {
        this.#subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe());
    }

    setBook(bookUuid: string): void {
        const book: BookListItemInterface | undefined = this.#state.books.find((book: BookListItemInterface) => bookUuid === book.uuid);

        if (undefined === book) {
            throw Error(`Failed to select book, Book(${bookUuid}) not found in collection`);
        }

        // Clear state but leave books intact
        this.#stateSubject.next(this.#state = {book, books: this.#state.books, exams: [], groups: [], loading: true});

        this.#handleBook(bookUuid).subscribe(() => this.#stateSubject.next(this.#state = {...this.#state, loading: false}));
    }

    setExam(examId: number): void {
        const exam: ApiExamInterface | undefined = this.#state.exams.find((exam: ApiExamInterface) => examId === exam.id);

        if (undefined === exam) {
            throw Error(`Failed to set exam, Exam(${examId}) not found in collection`);
        }

        this.#stateSubject.next(this.#state = {...this.#state, exam: exam});
    }

    submit(mode: HttpMethodsEnum.Put | HttpMethodsEnum.Post, bookUuid: string, exam: ExamAssignInterface): Observable<AssignDialogAssignResultInterface> {
        this.#stateSubject.next(this.#state = {...this.#state, loading: true});
        this.#examService.assign(mode, bookUuid, exam).subscribe(
            () => this.#handleAssignSuccess(),
            (errorResponse: HttpErrorResponseInterface) => this.#handleAssignFailed(errorResponse.error),
        );

        return this.#assignSubject.pipe(take(1));
    }

    delete(examId: string): Observable<AssignDialogAssignResultInterface> {
        this.#stateSubject.next(this.#state = {...this.#state, loading: true});
        this.#examService.delete(examId).subscribe(
            () => this.#handleAssignSuccess(),
            (errorResponse: HttpErrorResponseInterface) => this.#handleAssignFailed(errorResponse.error),
        );

        return this.#assignSubject.pipe(take(1));
    }

    /**
     * TODO: We have to fix the issue where the backend changes all error codes to 500 so that we can read the message and pass it as validation errors to the form
     */
    #handleAssignFailed(errorResponse: HttpErrorResponse|HttpValidationErrorResponseInterface): void {
        this.#stateSubject.next(this.#state = {...this.#state, loading: false});
        this.#snackBar.open(this.#translateService.instant('exam.dialog.assign.failed'), 'error', {duration: 3000, horizontalPosition: 'right', verticalPosition: 'top'});

        this.#assignSubject.next({status: false, errorResponse});
    }

    #handleAssignSuccess(): void {
        this.#stateSubject.next(this.#state = {...this.#state, loading: false});
        this.#snackBar.open(this.#translateService.instant('exam.dialog.assign.success'), 'success', {duration: 3000, horizontalPosition: 'right', verticalPosition: 'top'});

        this.#assignSubject.next({status: true});
    }
}
